/**
 * React hook for device communication
 *
 * Provides easy access to device communication service
 * with state management and error handling.
 */

import { useState, useCallback } from 'react';
import {
  deviceCommunicationService,
  DeviceInfo,
  DeviceOperation,
  DeviceOperationResult,
  CommunicationMode,
  OperationSource,
} from '../services/deviceCommunication';
import { logger } from '../services/logger';

/**
 * Device communication hook state
 */
export interface UseDeviceCommunicationState {
  isLoading: boolean;
  lastResult?: DeviceOperationResult;
  error?: string;
}

/**
 * Device communication hook return type
 */
export interface UseDeviceCommunicationReturn {
  /**
   * Execute a device operation
   */
  execute: (
    device: DeviceInfo,
    operation: DeviceOperation,
    payload: any,
    mode?: CommunicationMode
  ) => Promise<DeviceOperationResult>;

  /**
   * Execute operations on multiple devices in parallel
   */
  executeMultiple: (
    devices: DeviceInfo[],
    operation: DeviceOperation,
    payload: any,
    mode?: CommunicationMode
  ) => Promise<DeviceOperationResult[]>;

  /**
   * Current state
   */
  state: UseDeviceCommunicationState;

  /**
   * Clear error
   */
  clearError: () => void;

  /**
   * Get summary of last operation
   */
  getLastOperationSummary: () => {
    success: boolean;
    source?: OperationSource;
    latencyMs?: number;
    mode?: CommunicationMode;
  } | null;
}

/**
 * Hook for device communication with state management
 */
export function useDeviceCommunication(): UseDeviceCommunicationReturn {
  const [state, setState] = useState<UseDeviceCommunicationState>({
    isLoading: false,
    lastResult: undefined,
    error: undefined,
  });

  /**
   * Execute a single device operation
   */
  const execute = useCallback(
    async (
      device: DeviceInfo,
      operation: DeviceOperation,
      payload: any,
      mode: CommunicationMode = CommunicationMode.HYBRID
    ): Promise<DeviceOperationResult> => {
      setState((prev) => ({
        ...prev,
        isLoading: true,
        error: undefined,
      }));

      try {
        logger.debug('useDeviceComm', 'Executing operation', {
          deviceId: device.id,
          operationType: operation.type,
          mode,
        });

        const result = await deviceCommunicationService.executeOperation(
          device,
          operation,
          payload,
          mode
        );

        setState({
          isLoading: false,
          lastResult: result,
          error: result.success ? undefined : result.error,
        });

        return result;
      } catch (error: any) {
        const errorMessage = error.message || 'Device operation failed';

        logger.error('useDeviceComm', 'Operation failed', {
          deviceId: device.id,
          error: errorMessage,
        });

        setState({
          isLoading: false,
          lastResult: undefined,
          error: errorMessage,
        });

        throw error;
      }
    },
    []
  );

  /**
   * Execute operations on multiple devices in parallel
   */
  const executeMultiple = useCallback(
    async (
      devices: DeviceInfo[],
      operation: DeviceOperation,
      payload: any,
      mode: CommunicationMode = CommunicationMode.HYBRID
    ): Promise<DeviceOperationResult[]> => {
      setState((prev) => ({
        ...prev,
        isLoading: true,
        error: undefined,
      }));

      try {
        logger.debug('useDeviceComm', 'Executing multiple operations', {
          deviceCount: devices.length,
          operationType: operation.type,
          mode,
        });

        const results = await Promise.all(
          devices.map((device) =>
            deviceCommunicationService.executeOperation(
              device,
              operation,
              payload,
              mode
            )
          )
        );

        // Get summary of results
        const successCount = results.filter((r) => r.success).length;
        const failCount = results.length - successCount;

        logger.info('useDeviceComm', 'Multiple operations completed', {
          total: results.length,
          success: successCount,
          failed: failCount,
        });

        setState({
          isLoading: false,
          lastResult: results[results.length - 1], // Last result
          error: failCount > 0 ? `${failCount} operation(s) failed` : undefined,
        });

        return results;
      } catch (error: any) {
        const errorMessage = error.message || 'Multiple device operations failed';

        logger.error('useDeviceComm', 'Multiple operations failed', {
          deviceCount: devices.length,
          error: errorMessage,
        });

        setState({
          isLoading: false,
          lastResult: undefined,
          error: errorMessage,
        });

        throw error;
      }
    },
    []
  );

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setState((prev) => ({
      ...prev,
      error: undefined,
    }));
  }, []);

  /**
   * Get summary of last operation
   */
  const getLastOperationSummary = useCallback(() => {
    if (!state.lastResult) {
      return null;
    }

    return {
      success: state.lastResult.success,
      source: state.lastResult.source,
      latencyMs: state.lastResult.latencyMs,
      mode: state.lastResult.mode,
    };
  }, [state.lastResult]);

  return {
    execute,
    executeMultiple,
    state,
    clearError,
    getLastOperationSummary,
  };
}

/**
 * Helper hook for quick device operations without state management
 */
export function useQuickDeviceOperation() {
  const execute = useCallback(
    async (
      device: DeviceInfo,
      operation: DeviceOperation,
      payload: any,
      mode: CommunicationMode = CommunicationMode.HYBRID
    ): Promise<DeviceOperationResult> => {
      return deviceCommunicationService.executeOperation(
        device,
        operation,
        payload,
        mode
      );
    },
    []
  );

  return { execute };
}
